import { useEffect, useRef, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listAdminEvents,
  saveAdminEvent,
  deleteAdminEvent,
  listEventAttendees,
  addEventAttendee,
  searchCustomersForEvent,
  listEventTemplates,
  type EventTemplate,
  type CalendarEvent,
  type EventAttendee,
  type CustomerSearchResult,
  listDistilleries,
  type DistilleryOption,
  uploadEventImage,
  listEventImageUrls,
  getEmailStats,
  type EmailStats,
  listEventSeries,
  createEventSeries,
  type EventSeriesOption,
  saveEventOfflineTotals,
  syncSquareTickets,
  checkPublishCredentials,
  type PublishCredentials,
  type AnnouncementPayload,
  type AnnouncementResult,
  listPartners,
  loadEventPartners,
  saveEventPartners, updateSquareCatalog, createCheckoutLink, syncSquareCapacity, publishSocialEvent, sendEventAnnouncement
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";
import { getCachedUser } from "../../lib/auth";
import { useIsMobile } from "../../lib/useIsMobile";
import { mdToHtml, wrapSelection, prefixLine, insertLink, type MdEdit } from "../../lib/markdown";

// ── Slug (mirrors the original Library events admin) ──────────────────────
// The public site serves /events/<slug>, and both publish-event (social posts)
// and send-event-announcement (email CTA) link to it. Station used to omit slug
// entirely, so new events landed with slug=null and every off-site link broke.
function slugifyTitle(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
// Recurring events share a title, so stamp the local start date onto new slugs
// (e.g. whiskey-wednesday-2026-08-13). Uses the picked YYYY-MM-DD directly —
// no toISOString(), so it can't shift a day across the UTC boundary.
function eventDateSuffix(startDate: string): string {
  const m = String(startDate || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? "-" + m[1] : "";
}
function slugHasDateSuffix(s: string | null | undefined): boolean {
  return /-\d{4}-\d{2}-\d{2}$/.test(s || "");
}

// ── Venue picker (mirrors the original Library events admin) ───────────────
const LOCATION_OPTIONS = [
  "Sentinel's Reserve Whiskey Library",
  "Sentinel Room Cocktail Lounge",
  "The Sentinel",
  "Spirit Of Independence Fest",
];
const LOCATION_OTHER = "Other";
const DEFAULT_LOCATION = LOCATION_OPTIONS[0];
const DEFAULT_ADDRESSES: Record<string, string> = {
  "Sentinel's Reserve Whiskey Library": "208 W Lexington Ave, Independence, MO 64050",
  "Sentinel Room Cocktail Lounge": "208 W Lexington Ave, Independence, MO 64050",
  "The Sentinel": "208 W Lexington Ave, Independence, MO 64050",
  "The Marinello": "210 W Lexington Ave, Independence, MO 64050",
  "Spirit Of Independence Fest": "",
};

// ── C33 member note (metadata.c33_member_note) ─────────────────────────────
// false = block disabled · string = custom text · null/absent = default text.
// send-event-announcement renders this block for Collective 33 recipients.
const DEFAULT_C33_NOTE =
  "As a C33 member, this one hits a little different:\n\n• Priority access before tickets are released publicly\n• Your usual perks apply\n• Built-in advantage when access is limited";

const EVENT_TYPES = [
  { value: "tasting", label: "Tasting" },
  { value: "bottle_release", label: "Bottle Release" },
  { value: "festival", label: "Festival" },
  { value: "dinner", label: "Dinner" },
  { value: "class", label: "Class" },
  { value: "private", label: "Private Event" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "published", label: "Published" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
  { value: "past", label: "Past" },
  { value: "canceled", label: "Canceled" },
];

const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    TIME_OPTIONS.push(`${hh}:${mm}`);
  }
}

function formatTimeLabel(timeStr: string) {
  if (!timeStr) return "—";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}

function formatToChicagoDateTime(isoStr: string | null | undefined) {
  if (!isoStr) return { dateStr: "", timeStr: "18:00" };
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return { dateStr: "", timeStr: "18:00" };
  const dateStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  const timeStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Chicago",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
  return { dateStr, timeStr };
}

function getChicagoOffset(dateStr: string): string {
  try {
    const ref = new Date(`${dateStr}T12:00:00Z`);
    const offsetStr = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      timeZoneName: "shortOffset",
    }).format(ref);
    const match = offsetStr.match(/GMT([+-]\d+)(?::(\d+))?/);
    if (match) {
      const signHours = match[1];
      const sign = signHours.startsWith("-") ? "-" : "+";
      const hours = Math.abs(parseInt(signHours, 10)).toString().padStart(2, "0");
      const mins = (match[2] || "00").padStart(2, "0");
      return `${sign}${hours}:${mins}`;
    }
  } catch {}
  return "-05:00";
}

function chicagoToIsoString(dateStr: string, timeStr: string): string | null {
  if (!dateStr || !timeStr) return null;
  const offset = getChicagoOffset(dateStr);
  return `${dateStr}T${timeStr}:00${offset}`;
}

/** Tomorrow's calendar date in America/Chicago as YYYY-MM-DD. `toISOString()` is UTC, so after
 *  ~7 PM Central it already reads as the next day and "tomorrow" came out as the day after. */
function chicagoTomorrowDateStr(): string {
  const today = formatToChicagoDateTime(new Date().toISOString()).dateStr;
  const [y, m, d] = today.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().split("T")[0];
}

/** Date field for the event form. Desktop keeps the plain native input (typeable). On phones the
 *  native input is laid transparently over a styled box, so a tap lands directly on the input and
 *  opens the OS picker. This is the pattern Cash Management's date picker already uses and that
 *  works in the installed Station app on Android Chrome, where the plain styled input here did not. */
function DateField({
  value,
  onChange,
  required,
  isMobile,
}: {
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  isMobile: boolean;
}) {
  if (!isMobile) {
    return (
      <input
        type="date"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
      />
    );
  }
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const label = m
    ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "Select date";
  return (
    <div style={{ ...styles.input, position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 44, boxSizing: "border-box" }}>
      <span style={{ color: m ? "var(--text, #F7F5F0)" : "var(--muted, #A39D98)" }}>{label}</span>
      <span aria-hidden="true" style={{ fontSize: 15 }}>📅</span>
      <input
        type="date"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Select date"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: "none", padding: 0, margin: 0 }}
      />
    </div>
  );
}

export default function AdminEvents() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [distilleries, setDistilleries] = useState<DistilleryOption[]>([]);
  const [partnersList, setPartnersList] = useState<any[]>([]);
  const [eventPartners, setEventPartners] = useState<any[]>([]);
  const [distillerySearch, setDistillerySearch] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");

  // Venue: `editingEvent.location` holds the select value (a known venue or
  // "Other"); the free-text name and the address live here.
  const [locationOther, setLocationOther] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  // C33 note block
  const [c33NoteEnabled, setC33NoteEnabled] = useState(false);
  const [c33NoteText, setC33NoteText] = useState(DEFAULT_C33_NOTE);
  const isMobile = useIsMobile(768);
  // Series / tags / brand labels
  const [seriesList, setSeriesList] = useState<EventSeriesOption[]>([]);
  const [newSeriesOpen, setNewSeriesOpen] = useState(false);
  const [newSeriesTitle, setNewSeriesTitle] = useState("");
  const [tagsStr, setTagsStr] = useState("");
  // Offline totals (existing events only)
  const [offlineCash, setOfflineCash] = useState("");
  const [offlineCard, setOfflineCard] = useState("");
  const [manualAttendees, setManualAttendees] = useState("");
  const [offlineStatus, setOfflineStatus] = useState<{ text: string; kind: "ok" | "err" } | null>(null);
  const [offlineSaving, setOfflineSaving] = useState(false);
  const [syncingTickets, setSyncingTickets] = useState(false);
  // Publish popover (card) + credential status cached for the session
  const [publishMenu, setPublishMenu] = useState<{ ev: CalendarEvent; x: number; y: number } | null>(null);
  const [publishCreds, setPublishCreds] = useState<PublishCredentials | null>(null);
  // Description markdown toolbar
  const descRef = useRef<HTMLTextAreaElement | null>(null);
  // Communications (existing events only — mirrors the original's comms section)
  const [sendEmailOnSave, setSendEmailOnSave] = useState(false);
  const [sendSmsOnSave, setSendSmsOnSave] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");
  const [announceAudience, setAnnounceAudience] = useState<"all" | "c33" | "insiders">("all");
  const [correctionNote, setCorrectionNote] = useState("");
  const [commsStatus, setCommsStatus] = useState<{ text: string; kind: "ok" | "err" | "info" } | null>(null);
  const [commsBusy, setCommsBusy] = useState(false);
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  // Event image
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerLoading, setImagePickerLoading] = useState(false);
  const [imagePickerUrls, setImagePickerUrls] = useState<string[]>([]);
    
  const [searchQuery, setSearchQuery] = useState("");

  // Editor Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Partial<CalendarEvent> | null>(null);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("18:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("21:00");
  const [priceStr, setPriceStr] = useState("0.00");
  const [c33PriceStr, setC33PriceStr] = useState("");
  const [descPreview, setDescPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showIndyEvents, setShowIndyEvents] = useState(false);

  // Attendees Drawer & Customer Search
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState<CustomerSearchResult[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeEmail, setAttendeeEmail] = useState("");
  const [attendeePhone, setAttendeePhone] = useState("");
  const [attendeeQty, setAttendeeQty] = useState(1);
  const [attendeeTier, setAttendeeTier] = useState("general");
  const [attendeeNotes, setAttendeeNotes] = useState("");
  const [submittingAttendee, setSubmittingAttendee] = useState(false);

  // Dedicated Guest Roster Modal (Quick View for staff running event)
  const [rosterModalEvent, setRosterModalEvent] = useState<CalendarEvent | null>(null);
  const [rosterSearch, setRosterSearch] = useState("");

  // Toasts
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isFormOpen) setIsFormOpen(false);
        if (rosterModalEvent) setRosterModalEvent(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFormOpen, rosterModalEvent]);

  // Customer search debounce with in-flight cancellation
  useEffect(() => {
    let cancelled = false;
    const q = customerSearchQuery.trim();
    if (q.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const results = await searchCustomersForEvent(q);
        if (!cancelled) setCustomerSearchResults(results);
      } catch (err) {
        console.error("search error:", err);
      } finally {
        if (!cancelled) setSearchingCustomers(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerSearchQuery]);

  useEffect(() => {
    listDistilleries()
      .then(setDistilleries)
      .catch((e) => {
        console.error(e);
        showToast("Distillery list failed to load — " + (e?.message || "unknown error"), "err");
      });
    listEventSeries()
      .then(setSeriesList)
      .catch((e) => {
        console.error(e);
        showToast("Event series failed to load — " + (e?.message || "unknown error"), "err");
      });
    listPartners().then(setPartnersList).catch(console.error);
  }, []);
  const loadEvents = async () => {
    setLoading(true);
    try {
      const [data, tpls] = await Promise.all([
        listAdminEvents(statusFilter),
        listEventTemplates().catch(() => [])
      ]);
      setEvents(data || []);
      setTemplates(tpls || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load events", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [statusFilter]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        (e.location && e.location.toLowerCase().includes(q)) ||
        (e.tags && e.tags.some((t) => t.toLowerCase().includes(q)))
    );
  }, [events, searchQuery]);

  const applyTemplate = (templateId: string) => {
    if (!templateId || !editingEvent) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    
    // Only a template that names a venue changes the venue (matches the original:
    // a template without one leaves the current venue and address alone).
    let tplLocation = editingEvent.location;
    if (tpl.location) {
      const tplKnown = LOCATION_OPTIONS.includes(tpl.location);
      setLocationOther(tplKnown ? "" : tpl.location);
      setLocationAddress(DEFAULT_ADDRESSES[tpl.location] || "");
      tplLocation = tplKnown ? tpl.location : LOCATION_OTHER;
    }
    // Only overwrite fields the template actually sets (original: `if (t.<field>)`),
    // so a template with an empty description doesn't blank text already typed.
    const next: Partial<CalendarEvent> = { ...editingEvent, location: tplLocation };
    if (tpl.title_pattern) next.title = tpl.title_pattern;
    if (tpl.event_type) next.event_type = tpl.event_type;
    if (tpl.default_capacity) next.capacity = tpl.default_capacity;
    if (tpl.default_rsvp_enabled !== undefined && tpl.default_rsvp_enabled !== null) {
      next.rsvp_type = tpl.default_rsvp_enabled ? "rsvp" : "none";
    }
    if (tpl.default_tags?.length) {
      next.tags = tpl.default_tags;
      setTagsStr(tpl.default_tags.join(", "));
    }
    if (tpl.description_template) next.description = tpl.description_template;
    setEditingEvent(next);
    // Price: only when the template carries one (a null default used to overwrite a typed price with "0.00").
    if (tpl.default_price_cents !== null && tpl.default_price_cents !== undefined) {
      setPriceStr((tpl.default_price_cents / 100).toFixed(2));
    }
    // C33 price: the original only fills it when the template has one; it never clears it.
    if (tpl.default_c33_price_cents) {
      setC33PriceStr((tpl.default_c33_price_cents / 100).toFixed(2));
    }
  };

  const resetCommsState = () => {
    setSendEmailOnSave(false);
    setSendSmsOnSave(false);
    setCorrectionNote("");
    setCommsStatus(null);
    setCommsBusy(false);
    setEmailStats(null);
    setAnnounceAudience("all");
    // Default the preview address to the signed-in manager, like the original.
    setPreviewEmail((prev) => prev || getCachedUser()?.email || "");
  };

  const loadEmailStats = async (eventId: string) => {
    setEmailStats(await getEmailStats(eventId));
  };

  const resetPhase3State = () => {
    setTagsStr("");
    setNewSeriesOpen(false);
    setNewSeriesTitle("");
    setOfflineCash("");
    setOfflineCard("");
    setManualAttendees("");
    setOfflineStatus(null);
  };

  const handleNewEvent = () => {
    resetCommsState();
    resetPhase3State();
    setEventPartners([]);
    const dateStr = chicagoTomorrowDateStr();

    setLocationOther("");
    setLocationAddress(DEFAULT_ADDRESSES[DEFAULT_LOCATION]);
    setC33NoteEnabled(false);
    setC33NoteText(DEFAULT_C33_NOTE);
    setImagePickerOpen(false);
    setEditingEvent({
      title: "",
      event_type: "tasting",
      status: "draft",
      location: DEFAULT_LOCATION,
      capacity: 30,
      rsvp_type: "rsvp",
      contains_alcohol: true,
        age_requirement: true,
        public_at: "",
        distillery_ids: [],
      description: "",
      tags: [],
    });
    setStartDate(dateStr);
    setStartTime("18:00");
    setEndDate(dateStr);
    setEndTime("21:00");
    setPriceStr("0.00");
    setC33PriceStr("");
    setAttendees([]);
    setShowAddAttendee(false);
    setIsFormOpen(true);
  };

  const handleEditEvent = async (ev: CalendarEvent) => {
    loadEventPartners(ev.id || "").then(setEventPartners).catch(console.error);
    let rsvpType = ev.rsvp_type || "none";
    if (ev.ticket_required) rsvpType = "ticket";
    else if (ev.rsvp_enabled) rsvpType = "rsvp";
    else if (ev.donation_enabled && ev.planning_signal_enabled) rsvpType = "donation_signal";
    else if (ev.donation_enabled) rsvpType = "donation";
    else if (ev.planning_signal_enabled) rsvpType = "signal";
    // Venue + address (same rule as the original: a known venue selects itself,
    // anything else becomes "Other" with the name in the free-text field; the
    // legacy "212 W Lexington" address on the Library is replaced by the default).
    const loc = ev.location || DEFAULT_LOCATION;
    const isKnown = LOCATION_OPTIONS.includes(loc);
    setLocationOther(isKnown ? "" : loc);
    const storedAddr = ev.location_address || ev.metadata?.address || "";
    const legacyLibrary = loc === DEFAULT_LOCATION && storedAddr === "212 W Lexington Ave, Independence, MO 64050";
    setLocationAddress((!legacyLibrary && storedAddr) || DEFAULT_ADDRESSES[loc] || "");
    // C33 note
    const c33Meta = ev.metadata?.c33_member_note;
    setC33NoteEnabled(c33Meta !== false);
    setC33NoteText(typeof c33Meta === "string" ? c33Meta : DEFAULT_C33_NOTE);
    setImagePickerOpen(false);
    resetCommsState();
    resetPhase3State();
    setTagsStr((ev.tags || []).join(", "));
    setOfflineCash(ev.offline_cash_cents ? (ev.offline_cash_cents / 100).toFixed(2) : "");
    setOfflineCard(ev.offline_card_cents ? (ev.offline_card_cents / 100).toFixed(2) : "");
    setManualAttendees(ev.manual_attendee_count ? String(ev.manual_attendee_count) : "");
    if (ev.id) void loadEmailStats(ev.id);
    setEditingEvent({ ...ev, location: isKnown ? loc : LOCATION_OTHER, rsvp_type: rsvpType as CalendarEvent["rsvp_type"] });
    if (ev.start_at) {
      const { dateStr, timeStr } = formatToChicagoDateTime(ev.start_at);
      setStartDate(dateStr);
      setStartTime(timeStr);
    }
    if (ev.end_at) {
      const { dateStr, timeStr } = formatToChicagoDateTime(ev.end_at);
      setEndDate(dateStr);
      setEndTime(timeStr);
    } else {
      const { dateStr } = formatToChicagoDateTime(ev.start_at);
      setEndDate(dateStr);
      setEndTime("");
    }
    setPriceStr(ev.price_cents != null ? (ev.price_cents / 100).toFixed(2) : "0.00");
    setC33PriceStr(ev.c33_price_cents != null ? (ev.c33_price_cents / 100).toFixed(2) : "");
    setShowAddAttendee(false);
    setIsFormOpen(true);

    if (ev.id) {
      setLoadingAttendees(true);
      try {
        const attList = await listEventAttendees(ev.id);
        setAttendees(attList || []);
      } catch (e: any) {
        showToast(e.message || "Failed to load attendees", "err");
        setAttendees([]);
      } finally {
        setLoadingAttendees(false);
      }
    }
  };

  const handleSaveEvent = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingEvent || !editingEvent.title) return;
    setSaving(true);

    try {
      const startIso = chicagoToIsoString(startDate, startTime);
      const endIso = endDate && endTime ? chicagoToIsoString(endDate, endTime) : null;

      // Drop the loaded slug from the payload: it is only re-sent when the rule
      // below decides to (re)generate it, so legacy title-only slugs are never
      // echoed back and a null slug is never re-written as null.
      const { slug: existingSlug, ...editableFields } = editingEvent;
      const payload: Partial<CalendarEvent> = {
        ...editableFields,
        start_at: startIso || undefined,
        end_at: endIso,
        price_cents: Math.round(parseFloat(priceStr || "0") * 100),
        c33_price_cents: c33PriceStr ? Math.round(parseFloat(c33PriceStr) * 100) : null,
        rsvp_enabled: editingEvent.rsvp_type === "rsvp",
        ticket_required: editingEvent.rsvp_type === "ticket",
        donation_enabled: editingEvent.rsvp_type === "donation" || editingEvent.rsvp_type === "donation_signal",
        planning_signal_enabled: editingEvent.rsvp_type === "signal" || editingEvent.rsvp_type === "donation_signal",
      };

      if (payload.public_at === "") payload.public_at = null;

      // Venue + address + metadata. Merge metadata rather than replace it:
      // submit-form writes the whole jsonb column, and publish-event stores
      // gbp_post_id / fb_post_id / ig_media_id in there — the original admin
      // overwrote those on every re-save.
      const address = locationAddress.trim();
      payload.location = editingEvent.location === LOCATION_OTHER
        ? (locationOther.trim() || DEFAULT_LOCATION)
        : (editingEvent.location || DEFAULT_LOCATION);
      payload.location_address = address || null;
      const c33Text = c33NoteText.trim();
      payload.metadata = {
        ...(editingEvent.metadata || {}),
        address: address || null,
        // null = "use the default copy" so the email's default can evolve without
        // re-saving every event; a custom string is sent verbatim.
        c33_member_note: !c33NoteEnabled ? false : (c33Text && c33Text !== DEFAULT_C33_NOTE.trim() ? c33Text : null),
      };
      payload.image_url = editingEvent.image_url?.trim() || null;
      const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
      payload.tags = tags.length ? tags : null;
      payload.series_id = editingEvent.series_id || null;

      // New events, events already on the date-stamped scheme, and events that
      // have no slug at all (the ones Station created before this rule existed)
      // get a date-stamped slug. Pre-existing title-only slugs are left
      // untouched so their published URLs don't change.
      const isNew = !editingEvent.id;
      if (isNew || !existingSlug || slugHasDateSuffix(existingSlug)) {
        payload.slug = slugifyTitle(editingEvent.title) + eventDateSuffix(startDate);
      }
      if (isNew) {
        // "Station" (not the original's "Staff") so reports can tell which admin
        // created an event. Nothing downstream filters on the value — only
        // sync-community-events reads its own marker.
        payload.created_by = "Station";
        payload.published_at = payload.status === "published" ? new Date().toISOString() : null;
      }
      
        // Hold publication if ticket_required but no checkout link yet
        const manualCheckoutUrl = editingEvent.square_checkout_url?.trim();
        if (payload.ticket_required && payload.status === 'published' && !manualCheckoutUrl && (payload.price_cents || 0) > 0) {
          payload.status = 'draft';
          payload.published_at = null;
        }
        
        const saved = await saveAdminEvent(payload);
        if (saved && saved.id) {
          await saveEventPartners(saved.id, eventPartners);
          
          let alertMsg = "✓ Event '" + (saved.title || editingEvent.title) + "' saved!";
          
          // Update Square catalog if event already has a Square item
          if (editingEvent.square_catalog_object_id && editingEvent.square_checkout_url && (payload.price_cents || 0) > 0) {
            showToast("Updating Square Catalog...", "ok");
            try {
              await updateSquareCatalog({
                title: payload.title,
                price_cents: payload.price_cents,
                c33_price_cents: payload.c33_price_cents || null,
                square_catalog_object_id: editingEvent.square_catalog_object_id,
                contains_alcohol: payload.contains_alcohol,
                age_requirement: payload.age_requirement ? "21+" : "none",
              });
            } catch (e) { console.warn('Square update failed:', e); }
          }
          
          // Sync Square capacity
          if (editingEvent.square_catalog_object_id) {
            try {
              const capRes = await syncSquareCapacity(saved.id);
              if (!capRes.ok && !capRes.skipped) {
                console.warn('Square capacity sync failed:', capRes.error);
                alertMsg += " (Note: Square ticket stock did not sync: " + capRes.error + ")";
              }
            } catch (e) { console.warn('Square capacity sync failed:', e); }
          }
          
          // Auto-generate Square checkout link for Ticket Required events (new only)
          if (payload.ticket_required && !manualCheckoutUrl && !editingEvent.square_checkout_url && (payload.price_cents || 0) > 0) {
            showToast("Generating Square Ticket Link...", "ok");
            try {
              const linkRes = await createCheckoutLink({
                event_id: saved.id,
                title: payload.title,
                price_cents: payload.price_cents,
                c33_price_cents: payload.c33_price_cents || null,
                contains_alcohol: payload.contains_alcohol,
                age_requirement: payload.age_requirement ? "21+" : "none",
                capacity: payload.capacity || null,
                  donation_enabled: payload.donation_enabled || false,
              });
              if (linkRes.ok && linkRes.checkout_url) {
                alertMsg = "Event saved! Square checkout link generated.";
              } else {
                alertMsg = "Event saved as DRAFT — checkout link failed: " + (linkRes.error || "Unknown error") + ". Fix the issue and re-save to publish.";
              }
            } catch (linkErr) {
              alertMsg = "Event saved as DRAFT — checkout link generation failed. Fix the issue and re-save to publish.";
            }
          } else if (payload.ticket_required && (payload.price_cents || 0) <= 0) {
            alertMsg = "Event saved as DRAFT. Set a ticket price and re-save to generate the checkout link and publish.";
          }

          // Send-on-save announcements (existing events only, like the original:
          // audience "all", the function handles the C33 early-access window and
          // insider scheduling itself).
          if ((sendEmailOnSave || sendSmsOnSave) && editingEvent.id) {
            showToast("Sending announcements...", "ok");
            try {
              const ann = await sendEventAnnouncement({
                event_id: saved.id,
                audience: "all",
                send_email: sendEmailOnSave,
                send_sms: sendSmsOnSave,
              });
              if (ann.ok) {
                const parts: string[] = [];
                if (ann.email_sent) parts.push(`${ann.email_sent} emails`);
                if (ann.sms_sent) parts.push(`${ann.sms_sent} SMS`);
                if (ann.sms_scheduled) parts.push(`${ann.sms_scheduled} SMS scheduled`);
                if (ann.insider_scheduled) parts.push(`${ann.insider_scheduled} insider emails queued`);
                alertMsg += " Announcements: " + (parts.join(", ") || "queued") + ".";
              } else {
                alertMsg += " Announcement failed: " + (ann.error || "Unknown error");
              }
            } catch (annErr: any) {
              alertMsg += " Announcement send failed: " + (annErr?.message || "unknown error");
            }
          }

          showToast(alertMsg);
        } else {
          showToast("✓ Event '" + editingEvent.title + "' saved!");
        }
        
        setIsFormOpen(false);
        await loadEvents();
  
    } catch (err: any) {
      showToast(err.message || "Failed to save event", "err");
    } finally {
      setSaving(false);
    }
  };

  
  // ── Duplicate (original: clone minus identity/state fields, open as new) ──
  const handleDuplicateEvent = () => {
    if (!editingEvent?.id) return;
    const {
      id: _id, created_at: _c, updated_at: _u, published_at: _p, slug: _s, square_checkout_url: _q,
      square_catalog_object_id: _qc, metadata: _m, attendee_count: _ac, offline_cash_cents: _oc,
      offline_card_cents: _od, manual_attendee_count: _ma, ...rest
    } = editingEvent as CalendarEvent & { created_at?: string; updated_at?: string };
    setEditingEvent({ ...rest, title: `${editingEvent.title} (Copy)`, status: "draft", start_at: "", end_at: null, square_checkout_url: null });
    setAttendees([]);
    setShowAddAttendee(false);
    resetCommsState();
    setOfflineCash(""); setOfflineCard(""); setManualAttendees(""); setOfflineStatus(null);
    setStartDate(""); setEndDate(""); setStartTime("18:00"); setEndTime("21:00");
    // Partners, distilleries, venue, C33 note, image and tags carry over.
    showToast("Duplicated — set the new date and save to create the copy");
  };

  // ── Offline totals (routes through save-event like the original) ──
  const handleSaveOfflineTotals = async () => {
    if (!editingEvent?.id) return;
    setOfflineSaving(true);
    setOfflineStatus(null);
    const totals = {
      offline_cash_cents: Math.round(parseFloat(offlineCash || "0") * 100) || 0,
      offline_card_cents: Math.round(parseFloat(offlineCard || "0") * 100) || 0,
      manual_attendee_count: parseInt(manualAttendees || "0", 10) || 0,
    };
    try {
      await saveEventOfflineTotals(editingEvent.id, totals);
      setEditingEvent({ ...editingEvent, ...totals });
      setOfflineStatus({ text: "✓ Saved", kind: "ok" });
    } catch (err: any) {
      setOfflineStatus({ text: "Error: " + (err?.message || "Save failed"), kind: "err" });
    } finally {
      setOfflineSaving(false);
    }
  };

  // ── Square ticket sync (admin-backfill-tickets) ──
  const handleSyncSquareTickets = async () => {
    if (!editingEvent?.id) return;
    setSyncingTickets(true);
    try {
      const res = await syncSquareTickets(editingEvent.id);
      if (res.error) {
        showToast("Sync failed: " + res.error, "err");
      } else {
        showToast(`Synced: ${res.registrations_created ?? 0} new registrations (${res.matched ?? 0} orders matched, ${res.skipped_existing ?? 0} already existed)`);
        const attList = await listEventAttendees(editingEvent.id);
        setAttendees(attList || []);
      }
    } catch (err: any) {
      showToast("Sync error: " + (err?.message || "unknown"), "err");
    } finally {
      setSyncingTickets(false);
    }
  };

  // ── Publish popover (replaces the numbered window.prompt) ──
  const openPublishMenu = async (ev: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setPublishMenu({ ev, x: Math.min(e.clientX, window.innerWidth - 260), y: e.clientY + 4 });
    if (!publishCreds) setPublishCreds(await checkPublishCredentials());
  };
  useEffect(() => {
    if (!publishMenu) return;
    const close = () => setPublishMenu(null);
    const t = setTimeout(() => document.addEventListener("click", close), 50);
    return () => { clearTimeout(t); document.removeEventListener("click", close); };
  }, [publishMenu]);

  // ── Series create (inline, staff-gated save-series) ──
  const handleCreateSeries = async () => {
    const title = newSeriesTitle.trim();
    if (!title || !editingEvent) return;
    try {
      const created = await createEventSeries(title);
      setSeriesList((prev) => [...prev, created].sort((a, b) => a.title.localeCompare(b.title)));
      setEditingEvent({ ...editingEvent, series_id: created.id });
      setNewSeriesOpen(false);
      setNewSeriesTitle("");
      showToast("Series created: " + created.title);
    } catch (err: any) {
      showToast("Could not create series: " + (err?.message || "unknown"), "err");
    }
  };

  // ── Markdown toolbar ──
  const applyMdEdit = (edit: MdEdit) => {
    if (!editingEvent) return;
    setEditingEvent({ ...editingEvent, description: edit.value });
    requestAnimationFrame(() => {
      const ta = descRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    });
  };
  const mdWrap = (prefix: string, suffix = prefix) => {
    const ta = descRef.current; if (!ta) return;
    applyMdEdit(wrapSelection(ta.value, ta.selectionStart, ta.selectionEnd, prefix, suffix));
  };
  const mdLine = (prefix: string) => {
    const ta = descRef.current; if (!ta) return;
    applyMdEdit(prefixLine(ta.value, ta.selectionStart, prefix));
  };
  const mdLink = () => {
    const ta = descRef.current; if (!ta) return;
    const url = window.prompt("Enter URL:");
    if (!url) return;
    applyMdEdit(insertLink(ta.value, ta.selectionStart, ta.selectionEnd, url.trim()));
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !editingEvent) return;
    setImageUploading(true);
    try {
      const url = await uploadEventImage(file);
      setEditingEvent((prev) => (prev ? { ...prev, image_url: url } : prev));
      setImagePickerOpen(false);
      showToast("Image uploaded");
    } catch (err: any) {
      showToast(err?.message || "Upload failed", "err");
    } finally {
      setImageUploading(false);
    }
  };

  const toggleImagePicker = async () => {
    if (imagePickerOpen) { setImagePickerOpen(false); return; }
    setImagePickerOpen(true);
    setImagePickerLoading(true);
    try {
      setImagePickerUrls(await listEventImageUrls(events.map((ev) => ev.image_url)));
    } finally {
      setImagePickerLoading(false);
    }
  };

  const handleLocationSelect = (value: string) => {
    if (!editingEvent) return;
    setEditingEvent({ ...editingEvent, location: value });
    const def = DEFAULT_ADDRESSES[value];
    if (def) setLocationAddress(def);
    else if (value !== LOCATION_OTHER) setLocationAddress("");
  };

  // Card shortcut: open the editor, where the Communications section holds the
  // audience / preview / send controls (replaces the old window.prompt flow).
  const handleSendAnnouncement = (ev: CalendarEvent, e: any) => {
    e.stopPropagation();
    void handleEditEvent(ev);
  };

  const runComms = async (
    busyText: string,
    payload: Omit<AnnouncementPayload, "event_id">,
    onOk: (res: AnnouncementResult) => string
  ) => {
    if (!editingEvent?.id) { setCommsStatus({ text: "Save the event first.", kind: "err" }); return; }
    setCommsBusy(true);
    setCommsStatus({ text: busyText, kind: "info" });
    try {
      const res = await sendEventAnnouncement({ ...payload, event_id: editingEvent.id });
      if (res.ok) {
        setCommsStatus({ text: onOk(res), kind: "ok" });
        void loadEmailStats(editingEvent.id);
      } else {
        setCommsStatus({ text: res.error || "Send failed", kind: "err" });
      }
    } catch (err: any) {
      setCommsStatus({ text: err?.message || "Send failed", kind: "err" });
    } finally {
      setCommsBusy(false);
    }
  };

  const handlePreviewEmail = () => {
    const email = previewEmail.trim();
    if (!email) { setCommsStatus({ text: "Enter an email address for the preview.", kind: "err" }); return; }
    void runComms("Sending preview...", { audience: announceAudience, preview_email: email },
      () => `✓ Preview sent to ${email}`);
  };

  const AUDIENCE_LABEL: Record<"all" | "c33" | "insiders", string> = {
    all: "everyone", c33: "Collective 33 members", insiders: "Insiders (non-members)",
  };

  const handleSendNow = () => {
    if (!editingEvent?.id) return;
    if (!window.confirm(`Send the announcement for "${editingEvent.title}" to ${AUDIENCE_LABEL[announceAudience]} now?`)) return;
    void runComms(`Sending to ${AUDIENCE_LABEL[announceAudience]}...`, { audience: announceAudience }, (res) => {
      const parts: string[] = [];
      if (res.email_sent) parts.push(`${res.email_sent} email${res.email_sent !== 1 ? "s" : ""} sent`);
      if (res.email_failed) parts.push(`${res.email_failed} failed`);
      if (res.insider_scheduled) parts.push(`${res.insider_scheduled} insider emails queued`);
      return "✓ " + (parts.join(", ") || "Sent");
    });
  };

  const handleResendMissedInForm = () => {
    if (!editingEvent?.id) return;
    if (!window.confirm(`Check who missed the "${editingEvent.title}" announcement and send only to them?\n\nAnyone who already received it will be skipped.`)) return;
    void runComms("Finding missed recipients...", { audience: "all", resend_missed_only: true }, (res) => {
      if (!res.email_sent && !res.email_failed) return "✓ No missed recipients — everyone already received it";
      const parts: string[] = [];
      if (res.email_sent) parts.push(`${res.email_sent} email${res.email_sent !== 1 ? "s" : ""} sent`);
      if (res.email_failed) parts.push(`${res.email_failed} failed`);
      return "✓ Resend complete: " + parts.join(", ");
    });
  };

  const handleCorrectionPreview = () => {
    const email = previewEmail.trim();
    if (!email) { setCommsStatus({ text: "Enter an email address in the Preview Email field first.", kind: "err" }); return; }
    void runComms("Sending correction preview...", {
      action: "send_date_correction", preview_email: email, correction_note: correctionNote.trim() || undefined,
    }, () => `✓ Correction preview sent to ${email}`);
  };

  const handleCorrectionSend = () => {
    if (!editingEvent?.id) return;
    if (!window.confirm(`Send a Date & Time Correction email for "${editingEvent.title}" to ALL opted-in customers?\n\nThis will notify everyone that the date or time has changed.`)) return;
    void runComms("Sending correction emails...", {
      action: "send_date_correction", correction_note: correctionNote.trim() || undefined,
    }, (res) =>
      `✓ Correction sent — ${res.email_sent || 0} email${res.email_sent !== 1 ? "s" : ""} delivered${res.email_failed ? `, ${res.email_failed} failed` : ""}`);
  };

  const handleResendMissed = async (ev: CalendarEvent, e: any) => {
    e.stopPropagation();
    if (!ev.id) return;
    if (!window.confirm(`Check who missed the "${ev.title}" announcement and send only to them?\n\nAnyone who already received it will be skipped.`)) return;
    
    showToast("Finding missed recipients...");
    try {
      const res = await sendEventAnnouncement({ event_id: ev.id, audience: "all", resend_missed_only: true });
      if (res.ok) {
        const parts = [];
        if (res.email_sent) parts.push(`${res.email_sent} email${res.email_sent !== 1 ? 's' : ''} sent`);
        if (res.email_failed) parts.push(`${res.email_failed} failed`);
        if (!res.email_sent && !res.email_failed) parts.push("No missed recipients found — everyone already received it");
        window.alert(`Resend complete:\n\n${parts.join('\n')}`);
      } else {
        showToast("Resend failed: " + (res.error || "Unknown error"), "err");
      }
    } catch (err: any) {
      showToast("Resend error: " + err.message, "err");
    }
  };

  const handlePublishSocial = async (ev: CalendarEvent, action: string) => {
    showToast("Publishing to Social...");
    try {
      const res = await publishSocialEvent(ev.id!, action);
      if (res.ok) {
        const msgs = [];
        if (res.results?.gbp?.ok) msgs.push("Google ✓");
        if (res.results?.facebook?.ok) msgs.push("Facebook ✓");
        if (res.results?.gbp?.error) msgs.push("Google: " + res.results.gbp.error);
        if (res.results?.facebook?.error) msgs.push("Facebook: " + res.results.facebook.error);
        window.alert("Published:\n" + (msgs.join("\n") || "Done"));
        await loadEvents();
      } else {
        showToast("Publish failed: " + (res.error || "Unknown error"), "err");
      }
    } catch (err: any) {
      showToast("Publish error: " + err.message, "err");
    }
  };

  const handleCancelEvent = async (eventId: string, title: string) => {
    if (!window.confirm(`Are you sure you want to cancel "${title}"?`)) return;
    try {
      await deleteAdminEvent(eventId);
      showToast(`✓ Canceled "${title}"`);
      setIsFormOpen(false);
      await loadEvents();
    } catch (err: any) {
      showToast(err.message || "Failed to cancel event", "err");
    }
  };

  const handleSelectCustomer = (c: CustomerSearchResult) => {
    setSelectedCustomerId(c.id);
    setAttendeeName(c.name);
    setAttendeeEmail(c.email || "");
    setAttendeePhone(c.phone || "");
    setAttendeeTier(c.is_c33 ? "c33_member" : "general");
    setCustomerSearchResults([]);
    setCustomerSearchQuery("");
  };

  const handleAddAttendee = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingEvent?.id) return;
    if (!selectedCustomerId) {
      showToast("Please search and select a customer first", "err");
      return;
    }
    setSubmittingAttendee(true);
    try {
      await addEventAttendee({
        event_id: editingEvent.id,
        customer_id: selectedCustomerId,
        quantity: attendeeQty,
        ticket_tier: attendeeTier,
        price_override_cents: attendeeTier === "comp" ? 0 : undefined,
        notes: attendeeNotes.trim() || null,
      });
      showToast("✓ Attendee registered");
      setSelectedCustomerId(null);
      setAttendeeName("");
      setAttendeeEmail("");
      setAttendeePhone("");
      setAttendeeQty(1);
      setAttendeeTier("general");
      setAttendeeNotes("");
      setShowAddAttendee(false);
      const attList = await listEventAttendees(editingEvent.id || rosterModalEvent?.id || "");
      setAttendees(attList || []);
    } catch (err: any) {
      showToast(err.message || "Failed to register attendee", "err");
    } finally {
      setSubmittingAttendee(false);
    }
  };

  const handleOpenRoster = async (ev: CalendarEvent, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setRosterModalEvent(ev);
    setLoadingAttendees(true);
    setRosterSearch("");
    setShowAddAttendee(false);
    try {
      if (ev.id) {
        const attList = await listEventAttendees(ev.id);
        setAttendees(attList || []);
      }
    } catch (e: any) {
      showToast(e.message || "Failed to load attendees", "err");
      setAttendees([]);
    } finally {
      setLoadingAttendees(false);
    }
  };

  const handleToggleCheckIn = (attendeeId: string) => {
    setAttendees((prev) =>
      prev.map((a) => {
        if (a.id === attendeeId) {
          const isAttended = a.payment_status === "attended";
          const newStatus = isAttended ? "confirmed" : "attended";
          const newCheckedIn = isAttended ? null : new Date().toISOString();
          return {
            ...a,
            payment_status: newStatus,
            checked_in_at: newCheckedIn,
          };
        }
        return a;
      })
    );
    showToast("✓ Guest check-in updated");
  };

  const filteredRosterAttendees = useMemo(() => {
    if (!rosterSearch.trim()) return attendees;
    const q = rosterSearch.toLowerCase();
    return attendees.filter(
      (a) =>
        a.customer_name?.toLowerCase().includes(q) ||
        a.customer_email?.toLowerCase().includes(q) ||
        a.customer_phone?.toLowerCase().includes(q) ||
        a.ticket_tier?.toLowerCase().includes(q) ||
        a.notes?.toLowerCase().includes(q)
    );
  }, [attendees, rosterSearch]);

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="events" badgeCounts={{ events: events.length }} />
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Events & Master Schedule</h1>
          <p style={styles.subtitle}>
            Manage tastings, master classes, bottle releases, and private events with ticket caps & RSVPs.
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={handleNewEvent}>
          + New Event
        </button>
      </header>

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "ok" ? "rgba(39, 174, 96, 0.15)" : "rgba(224, 80, 64, 0.15)",
            borderColor: toast.type === "ok" ? "#27ae60" : "var(--red)",
            color: toast.type === "ok" ? "#86efac" : "var(--red)",
          }}
        >
          {toast.text}
        </div>
      )}

      <div style={styles.toolbar}>
        <div style={styles.filterPills}>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.pillBtn,
                ...(statusFilter === opt.value ? styles.pillBtnActive : {}),
              }}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search events by title, location, or tag..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {loading ? (
        <div style={styles.loader}>Loading scheduled events…</div>
      ) : (
        <div style={styles.eventsGrid}>
          {(() => {
            const sentinelEvents = filteredEvents.filter(ev => !ev.location?.toLowerCase().includes("independence square"));
            const indyEvents = filteredEvents.filter(ev => ev.location?.toLowerCase().includes("independence square"));

            const renderEventCard = (ev: CalendarEvent) => {
              const dateDisplay = ev.start_at
                ? new Date(ev.start_at).toLocaleDateString("en-US", {
                    timeZone: "America/Chicago",
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "Date TBD";
              const timeDisplay = ev.start_at
                ? formatTimeLabel(formatToChicagoDateTime(ev.start_at).timeStr)
                : "";
              const price = ev.price_cents ? `$${(ev.price_cents / 100).toFixed(2)}` : "Free";

              return (
                <div key={ev.id} style={styles.eventCard} onClick={() => handleEditEvent(ev)}>
                  <div style={styles.cardHeader}>
                    <span style={styles.typeBadge}>{ev.event_type}</span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background:
                          ev.status === "published"
                            ? "rgba(39, 174, 96, 0.15)"
                            : ev.status === "draft"
                            ? "rgba(249, 175, 54, 0.15)"
                            : "rgba(163, 157, 152, 0.15)",
                        color:
                          ev.status === "published"
                            ? "#86efac"
                            : ev.status === "draft"
                            ? "var(--gold)"
                            : "var(--muted)",
                      }}
                    >
                      {ev.status}
                    </span>
                  </div>

                  <h3 style={styles.cardTitle}>{ev.title}</h3>
                  <div style={styles.dateTimeRow}>
                    <span>📅 {dateDisplay}</span>
                    {timeDisplay && <span>🕒 {timeDisplay}</span>}
                  </div>

                  <div style={styles.locationText}>📍 {ev.location || "Sentinel Library"}</div>

                  <div style={styles.cardFooter}>
                    <div style={styles.cardPrice}>
                      {price}
                      {ev.c33_price_cents != null && (
                        <span style={styles.c33Tag}>C33: ${(ev.c33_price_cents / 100).toFixed(2)}</span>
                      )}
                    </div>
                    <div style={styles.capacityTag}>
                      👥 {ev.capacity ? `${ev.capacity} seats` : "Unlimited"}
                    </div>
                  </div>

                  
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                      {ev.ticket_required && <span style={{...styles.statusBadge, background: "rgba(249,175,54,0.15)", color: "var(--gold)"}}>🎟️ Ticket</span>}
                      {ev.square_checkout_url && <span style={{...styles.statusBadge, background: "rgba(52,152,219,0.12)", color: "#5dade2"}}>Square ✓</span>}
                      {(ev as any).metadata?.gbp_post_id && <span style={{...styles.statusBadge, background: "rgba(52,152,219,0.12)", color: "#5dade2"}}>GBP ✓</span>}
                      {(ev as any).metadata?.fb_post_id && <span style={{...styles.statusBadge, background: "rgba(59,89,152,0.12)", color: "#8b9dc3"}}>FB ✓</span>}
                      
                      {ev.status === "published" && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => handleSendAnnouncement(ev, e)}
                            style={{...styles.statusBadge, background: "rgba(249,175,54,0.15)", color: "var(--gold)", border: "none", cursor: "pointer"}}
                          >📧 Send Announcement</button>
                          <button
                            type="button"
                            onClick={(e) => handleResendMissed(ev, e)}
                            style={{...styles.statusBadge, background: "rgba(224,80,64,0.12)", color: "var(--red)", border: "none", cursor: "pointer"}}
                          >↩ Resend Missed</button>
                          <button
                            type="button"
                            onClick={(e) => void openPublishMenu(ev, e)}
                            style={{...styles.statusBadge, background: "rgba(39,174,96,0.12)", color: "var(--green)", border: "none", cursor: "pointer"}}
                          >📢 Publish ▾</button>
                        </>
                      )}
                    </div>

                    <div style={styles.cardActionsRow}>
                    <button
                      type="button"
                      style={styles.cardRosterBtn}
                      onClick={(e) => handleOpenRoster(ev, e)}
                    >
                      🎟️ View Guest List & Tickets
                    </button>
                    <button
                      type="button"
                      style={styles.cardEditBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent(ev);
                      }}
                    >
                      ✏️ Specs
                    </button>
                  </div>
                </div>
              );
            };

            return (
              <>
                {sentinelEvents.map(renderEventCard)}
                
                {indyEvents.length > 0 && (
                  <div style={{ gridColumn: "1 / -1", marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "24px", display: "flex", justifyContent: "center" }}>
                    <button 
                      onClick={() => setShowIndyEvents(!showIndyEvents)}
                      style={{ ...styles.btnSecondary, background: "rgba(255,255,255,0.05)" }}
                    >
                      {showIndyEvents ? "Hide Independence Square Events" : `View ${indyEvents.length} Independence Square Events`}
                    </button>
                  </div>
                )}

                {showIndyEvents && indyEvents.map(renderEventCard)}

                {filteredEvents.length === 0 && (
                  <div style={styles.emptyState}>No events found matching your filter criteria.</div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {publishMenu && (() => {
        const creds = publishCreds;
        const fbIssue = creds?.facebook_error
          ? (creds.facebook_error_code === "scope" ? "Missing permissions — re-mint token"
            : creds.facebook_error_code === "expired" ? "Token expired — rotate"
            : "Token rejected — check FB app")
          : "Not configured";
        const anyConfigured = !!(creds?.gbp || creds?.facebook || creds?.instagram);
        const opt = (label: string, action: string, enabled: boolean, issue?: string) => (
          <button
            type="button"
            key={action}
            disabled={!enabled}
            onClick={() => { setPublishMenu(null); void handlePublishSocial(publishMenu.ev, action); }}
            style={{ display: "flex", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", padding: "8px 16px", fontSize: 13, background: "transparent", border: "none", cursor: enabled ? "pointer" : "not-allowed", color: enabled ? "var(--text, #F7F5F0)" : "var(--muted, #A39D98)", opacity: enabled ? 1 : 0.6, fontFamily: "inherit" }}
          >
            <span>{label}</span>
            {!enabled && creds && <span style={{ fontSize: 10, color: "var(--red, #e05040)" }}>{issue}</span>}
          </button>
        );
        return (
          <div
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "fixed", top: publishMenu.y, left: publishMenu.x, zIndex: 9999, background: "var(--surface, #15110E)", border: "1px solid var(--border, #231D19)", borderRadius: 8, padding: "6px 0", minWidth: 240, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
          >
            <div style={{ padding: "6px 16px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted, #A39D98)" }}>
              Publish {publishMenu.ev.title.slice(0, 30)}{!creds ? " · checking…" : ""}
            </div>
            {opt("📍 Google Business Profile", "publish-gbp", !!creds?.gbp, "Not configured")}
            {opt("📘 Facebook Page", "publish-facebook", !!creds?.facebook, fbIssue)}
            {opt("📸 Instagram", "publish-instagram", !!creds?.instagram, fbIssue)}
            <div style={{ borderTop: "1px solid var(--border, #231D19)", margin: "4px 0" }} />
            {opt("📢 Publish Everywhere", "publish-all", anyConfigured, "Nothing configured")}
          </div>
        );
      })()}

      {isFormOpen && editingEvent && (
        <div style={styles.modalBackdrop} onClick={() => setIsFormOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingEvent.id ? `Edit Event: ${editingEvent.title}` : "Create New Event"}
              </h2>
              <button style={styles.modalClose} onClick={() => setIsFormOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveEvent} style={styles.formScroll}>
              <div className="station-form-grid" style={{ ...styles.formGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
                {!editingEvent.id && templates.length > 0 && (
                  <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1", marginBottom: 16 }}>
                    <label style={{ ...styles.label, color: "var(--gold, #D4AF37)" }}>Load from Template</label>
                    <select
                      style={styles.select}
                      onChange={(e) => applyTemplate(e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>Select a template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Event Title *</label>
                  <input
                    type="text"
                    required
                    value={editingEvent.title || ""}
                    onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                    style={styles.input}
                    placeholder="e.g. Master Class: Japanese Whisky Distillation"
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Event Type</label>
                  <select
                    value={editingEvent.event_type || "tasting"}
                    onChange={(e) => setEditingEvent({ ...editingEvent, event_type: e.target.value })}
                    style={styles.select}
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={editingEvent.status || "draft"}
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        status: e.target.value as CalendarEvent["status"],
                      })
                    }
                    style={styles.select}
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending Review</option>
                    <option value="published">Published</option>
                    <option value="past">Past</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Series (recurring)</label>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <select
                      value={editingEvent.series_id || ""}
                      onChange={(e) => setEditingEvent({ ...editingEvent, series_id: e.target.value || null })}
                      style={{ ...styles.select, flex: 1, minWidth: 0 }}
                      aria-label="Event series"
                    >
                      <option value="">— None (one-off event) —</option>
                      {seriesList.map((sr) => <option key={sr.id} value={sr.id}>{sr.title}</option>)}
                    </select>
                    <button type="button" style={styles.btnSmall} onClick={() => setNewSeriesOpen(!newSeriesOpen)}>+ New</button>
                  </div>
                  {newSeriesOpen && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="text"
                        placeholder='New series name (e.g. "Whiskey Wednesday")'
                        value={newSeriesTitle}
                        onChange={(e) => setNewSeriesTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleCreateSeries(); } }}
                        style={{ ...styles.input, flex: 1, minWidth: 0 }}
                        aria-label="New series name"
                      />
                      <button type="button" style={styles.btnSmall} onClick={() => void handleCreateSeries()}>Create</button>
                    </div>
                  )}
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Tags (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="s3, bourbon, charity"
                    value={tagsStr}
                    onChange={(e) => setTagsStr(e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Start Date *</label>
                  <DateField
                    required
                    isMobile={isMobile}
                    value={startDate}
                    onChange={(next) => {
                      setStartDate(next);
                      if (!endDate || endDate === startDate) setEndDate(next);
                    }}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Start Time *</label>
                  <select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    style={styles.select}
                  >
                    {TIME_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {formatTimeLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>End Date</label>
                  <DateField isMobile={isMobile} value={endDate} onChange={setEndDate} />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>End Time</label>
                  <select
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">—</option>
                    {TIME_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {formatTimeLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Location</label>
                  <select
                    value={editingEvent.location || DEFAULT_LOCATION}
                    onChange={(e) => handleLocationSelect(e.target.value)}
                    style={styles.select}
                  >
                    {LOCATION_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    <option value={LOCATION_OTHER}>Other</option>
                  </select>
                  {editingEvent.location === LOCATION_OTHER && (
                    <input
                      type="text"
                      placeholder="Custom location name"
                      value={locationOther}
                      onChange={(e) => setLocationOther(e.target.value)}
                      style={styles.input}
                    />
                  )}
                  <input
                    type="text"
                    placeholder="Address"
                    value={locationAddress}
                    onChange={(e) => setLocationAddress(e.target.value)}
                    style={styles.input}
                    aria-label="Location address"
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Capacity (Max Guests)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={editingEvent.capacity ?? ""}
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        capacity: e.target.value ? parseInt(e.target.value, 10) : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Registration Mode</label>
                  <select
                    value={editingEvent.rsvp_type || "rsvp"}
                    onChange={(e) =>
                      setEditingEvent({
                        ...editingEvent,
                        rsvp_type: e.target.value as CalendarEvent["rsvp_type"],
                      })
                    }
                    style={styles.select}
                  >
                    <option value="rsvp">RSVP (Free reservation)</option>
                    <option value="signal">Planning Signal (Headcount only)</option>
                    <option value="ticket">Ticket Required (Square Checkout)</option>
                    <option value="donation">Donation (Suggested amount)</option>
                    <option value="none">No Registration (Open door)</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>General Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceStr}
                    onChange={(e) => setPriceStr(e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>C33 Member Price ($ optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00 (or leave blank)"
                    value={c33PriceStr}
                    onChange={(e) => setC33PriceStr(e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={c33NoteEnabled}
                      onChange={(e) => setC33NoteEnabled(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: "#F9AF36" }}
                    />
                    Include a Collective 33 note in announcement emails
                  </label>
                  {c33NoteEnabled && (
                    <textarea
                      rows={5}
                      value={c33NoteText}
                      onChange={(e) => setC33NoteText(e.target.value)}
                      style={styles.textarea}
                      aria-label="Collective 33 member note (Markdown)"
                    />
                  )}
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Square Checkout Link</label>
                  <input
                    type="url"
                    placeholder="https://square.link/..."
                    value={editingEvent.square_checkout_url || ""}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, square_checkout_url: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 6 }}>
                    
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Public Release Date (C33 members get first dibs until this date)</label>
                    <input
                      type="datetime-local"
                      value={editingEvent.public_at ? editingEvent.public_at.slice(0, 16) : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditingEvent({ ...editingEvent, public_at: val ? new Date(val).toISOString() : null });
                      }}
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text)", fontSize: 14 }}>
                      <input type="checkbox" checked={editingEvent.contains_alcohol || false} onChange={e => setEditingEvent({...editingEvent, contains_alcohol: e.target.checked})} />
                      Contains Alcohol
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text)", fontSize: 14 }}>
                      <input type="checkbox" checked={editingEvent.age_requirement || false} onChange={e => setEditingEvent({...editingEvent, age_requirement: e.target.checked})} />
                      21+ Requirement
                    </label>
                  </div>
                  
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Distilleries</label><input type="text" placeholder="Search distilleries..." value={distillerySearch} onChange={e => setDistillerySearch(e.target.value)} style={{...styles.input, marginBottom: 8}} /><select
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const ids = editingEvent.distillery_ids || [];
                        if (!ids.includes(e.target.value)) {
                          setEditingEvent({ ...editingEvent, distillery_ids: [...ids, e.target.value] });
                        }
                        e.target.value = "";
                      }}
                      style={styles.input}
                    >
                      <option value="">+ Add distillery...</option>
                      {distilleries.filter(d => d.name.toLowerCase().includes(distillerySearch.toLowerCase())).slice(0, 50).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                      {(editingEvent.distillery_ids || []).map(id => {
                        const d = distilleries.find(x => x.id === id);
                        if (!d) return null;
                        return (
                          <span key={id} style={{ background: "rgba(249,175,54,0.15)", color: "var(--gold)", padding: "4px 8px", borderRadius: 4, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                            {d.name}
                            <button type="button" onClick={() => setEditingEvent({...editingEvent, distillery_ids: editingEvent.distillery_ids!.filter(x => x !== id)})} style={{ background: "transparent", border: "none", color: "var(--gold)", cursor: "pointer" }}>&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Partners / Sponsors</label><input type="text" placeholder="Search partners..." value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} style={{...styles.input, marginBottom: 8}} /><select
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const p = partnersList.find(x => x.id === e.target.value);
                        if (!p || eventPartners.some(ep => ep.partner_id === p.id)) return;
                        setEventPartners([...eventPartners, { partner_id: p.id, role: p.partner_type === "charity" ? "charity" : "co_host" }]);
                        e.target.value = "";
                      }}
                      style={styles.input}
                    >
                      <option value="">+ Add partner...</option>
                      {partnersList.filter(p => p.name.toLowerCase().includes(partnerSearch.toLowerCase())).slice(0, 50).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                      {eventPartners.map(ep => {
                        const p = partnersList.find(x => x.id === ep.partner_id);
                        if (!p) return null;
                        return (
                          <span key={ep.partner_id} style={{ background: "rgba(249,175,54,0.12)", color: "var(--gold)", border: "1px solid rgba(249,175,54,0.25)", padding: "4px 8px", borderRadius: 4, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                            {p.name}
                            <select value={ep.role} onChange={e => setEventPartners(eventPartners.map(x => x.partner_id === ep.partner_id ? {...x, role: e.target.value} : x))} style={{ background: "transparent", border: "none", color: "var(--gold)", fontSize: 11, cursor: "pointer", outline: "none" }}>
                              <option value="co_host">Co-host</option>
                              <option value="sponsor">Sponsor</option>
                              <option value="presenter">Presenter</option>
                              <option value="charity">Charity</option>
                              <option value="media_partner">Media Partner</option>
                              <option value="brand_ambassador">Brand Ambassador</option>
                            </select>
                            <button type="button" onClick={() => setEventPartners(eventPartners.filter(x => x.partner_id !== ep.partner_id))} style={{ background: "transparent", border: "none", color: "var(--gold)", cursor: "pointer" }}>&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <label style={styles.label}>Event Description (Markdown)</label>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }} role="toolbar" aria-label="Markdown formatting">
                      {!descPreview && (
                        <>
                          <button type="button" title="Bold" style={styles.mdBtn} onClick={() => mdWrap("**")}><strong>B</strong></button>
                          <button type="button" title="Italic" style={{ ...styles.mdBtn, fontStyle: "italic" }} onClick={() => mdWrap("*")}>I</button>
                          <button type="button" title="Heading" style={styles.mdBtn} onClick={() => mdLine("## ")}>H</button>
                          <button type="button" title="Bullet list" style={styles.mdBtn} onClick={() => mdLine("- ")}>•</button>
                          <button type="button" title="Numbered list" style={styles.mdBtn} onClick={() => mdLine("1. ")}>1.</button>
                          <button type="button" title="Link" style={styles.mdBtn} onClick={mdLink}>🔗</button>
                        </>
                      )}
                      <button
                        type="button"
                        style={styles.btnSmall}
                        onClick={() => setDescPreview(!descPreview)}
                      >
                        {descPreview ? "Edit Markdown" : "Preview"}
                      </button>
                    </div>
                  </div>
                  {descPreview ? (
                    editingEvent.description ? (
                      <div style={styles.previewBox} className="md-preview" dangerouslySetInnerHTML={{ __html: mdToHtml(editingEvent.description) }} />
                    ) : (
                      <div style={styles.previewBox}>No description.</div>
                    )
                  ) : (
                    <textarea
                      ref={descRef}
                      rows={8}
                      placeholder={"**Bold**, *italic*, - bullets, [link text](url)\n\n## Heading\n\nTasting flight list, master distiller bios, and event schedule..."}
                      value={editingEvent.description || ""}
                      onChange={(e) =>
                        setEditingEvent({ ...editingEvent, description: e.target.value })
                      }
                      style={styles.textarea}
                    />
                  )}
                </div>
              </div>

              <div style={{ ...styles.fieldGroup, marginTop: 14 }}>
                <label style={styles.label}>Event Image</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageFile}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    style={styles.btnSmall}
                    disabled={imageUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imageUploading ? "Uploading..." : "Upload New"}
                  </button>
                  <button type="button" style={styles.btnSmall} onClick={toggleImagePicker}>
                    {imagePickerOpen ? "Close Browser" : "Browse Existing"}
                  </button>
                  <input
                    type="url"
                    placeholder="https://... or upload"
                    value={editingEvent.image_url || ""}
                    onChange={(e) => setEditingEvent({ ...editingEvent, image_url: e.target.value })}
                    style={{ ...styles.input, flex: 1, minWidth: 200 }}
                    aria-label="Event image URL"
                  />
                </div>
                <div style={{ fontSize: 11, color: "var(--muted, #A39D98)" }}>
                  JPEG, PNG, WebP or GIF under 5MB. Instagram publishing requires an image; Google Business Profile attaches JPEG/PNG only.
                </div>
                {imagePickerOpen && (
                  <div style={{ marginTop: 8, maxHeight: 240, overflowY: "auto", border: "1px solid var(--border, #231D19)", borderRadius: 6, padding: 8, background: "var(--bg, #0C0907)" }}>
                    {imagePickerLoading ? (
                      <div style={{ fontSize: 13, color: "var(--muted, #A39D98)", padding: 12, textAlign: "center" }}>Loading images...</div>
                    ) : imagePickerUrls.length === 0 ? (
                      <div style={{ fontSize: 13, color: "var(--muted, #A39D98)", padding: 12, textAlign: "center" }}>No images found. Upload one first.</div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                        {imagePickerUrls.map((url) => {
                          const selected = editingEvent.image_url === url;
                          return (
                            <button
                              key={url}
                              type="button"
                              onClick={() => { setEditingEvent({ ...editingEvent, image_url: url }); setImagePickerOpen(false); }}
                              style={{ padding: 0, cursor: "pointer", border: `2px solid ${selected ? "var(--gold, #F9AF36)" : "transparent"}`, borderRadius: 4, overflow: "hidden", aspectRatio: "1", background: "transparent" }}
                              aria-label="Use this image"
                            >
                              <img src={url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {editingEvent.image_url && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <img
                      src={editingEvent.image_url}
                      alt="Event image preview"
                      style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 4, border: "1px solid var(--border, #231D19)" }}
                    />
                    <button type="button" style={styles.btnSmall} onClick={() => setEditingEvent({ ...editingEvent, image_url: null })}>
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {editingEvent.id && (
                <div style={styles.attendeesSection}>
                  <div style={styles.attendeesHeader}>
                    <h3 style={styles.sectionTitle}>Communications</h3>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
                    <div style={styles.fieldGroup}>
                      <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                        <input type="checkbox" checked={sendEmailOnSave} onChange={(e) => setSendEmailOnSave(e.target.checked)} style={{ width: 18, height: 18, accentColor: "#F9AF36" }} />
                        Send email announcement on save
                      </label>
                      <span style={styles.hint}>C33 members receive immediately. Insiders receive after early access expires.</span>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                        <input type="checkbox" checked={sendSmsOnSave} onChange={(e) => setSendSmsOnSave(e.target.checked)} style={{ width: 18, height: 18, accentColor: "#F9AF36" }} />
                        Send SMS notification on save
                      </label>
                      <span style={styles.hint}>SMS goes out 9 AM–5 PM CT. After hours = next business day.</span>
                    </div>
                  </div>

                  <div style={{ ...styles.fieldGroup, marginTop: 12 }}>
                    <label style={styles.label}>Preview Email</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={previewEmail}
                        onChange={(e) => setPreviewEmail(e.target.value)}
                        style={{ ...styles.input, flex: 1, minWidth: 200 }}
                        aria-label="Preview email address"
                      />
                      <button type="button" style={styles.btnSmall} disabled={commsBusy} onClick={handlePreviewEmail}>Send Preview</button>
                    </div>
                  </div>

                  <div style={{ ...styles.fieldGroup, marginTop: 12 }}>
                    <label style={styles.label}>Send Announcement Now</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <select
                        value={announceAudience}
                        onChange={(e) => setAnnounceAudience(e.target.value as "all" | "c33" | "insiders")}
                        style={{ ...styles.select, minWidth: 200 }}
                        aria-label="Announcement audience"
                      >
                        <option value="all">Everyone</option>
                        <option value="c33">Collective 33 members only</option>
                        <option value="insiders">Insiders (non-members) only</option>
                      </select>
                      <button type="button" style={styles.btnSmall} disabled={commsBusy} onClick={handleSendNow}>📧 Send to audience</button>
                      <button type="button" style={styles.btnSmall} disabled={commsBusy} onClick={handleResendMissedInForm}>↩ Resend to missed</button>
                    </div>
                    <span style={styles.hint}>Send Preview uses the audience selected here.</span>
                  </div>

                  <div style={{ marginTop: 12, background: "rgba(249,175,54,0.04)", border: "1px solid rgba(249,175,54,0.15)", borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold, #F9AF36)", letterSpacing: 1, marginBottom: 8 }}>DATE &amp; TIME CORRECTION</div>
                    <p style={{ fontSize: 13, color: "var(--muted, #A39D98)", margin: "0 0 10px" }}>Send an update email to all opted-in customers if the event date or time has changed.</p>
                    <textarea
                      rows={2}
                      value={correctionNote}
                      onChange={(e) => setCorrectionNote(e.target.value)}
                      placeholder="Optional: add a brief note (e.g. 'Apologies — we had a scheduling conflict and have moved the start time back 30 minutes.')"
                      style={{ ...styles.textarea, width: "100%", boxSizing: "border-box", marginBottom: 8 }}
                      aria-label="Correction note"
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" style={styles.btnSmall} disabled={commsBusy} onClick={handleCorrectionPreview}>Preview Correction Email</button>
                      <button type="button" style={{ ...styles.btnSmall, background: "rgba(249,175,54,0.15)", color: "#F9AF36", border: "1px solid rgba(249,175,54,0.35)" }} disabled={commsBusy} onClick={handleCorrectionSend}>Send Correction to All</button>
                    </div>
                  </div>

                  {commsStatus && (
                    <div role="status" style={{ fontSize: 13, marginTop: 8, minHeight: 18, color: commsStatus.kind === "ok" ? "var(--green, #27ae60)" : commsStatus.kind === "err" ? "var(--red, #e05040)" : "var(--muted, #A39D98)" }}>
                      {commsStatus.text}
                    </div>
                  )}

                  {emailStats && (
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border, #231D19)" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted, #A39D98)", marginBottom: 8 }}>EMAIL PERFORMANCE</div>
                      {(() => {
                        // A plain 0 here hid a dead webhook for six months; say so instead.
                        const s = emailStats.stats;
                        const broken = emailStats.tracking === "not_reporting";
                        // Sent but nothing tracked (e.g. the per-message insert failed): no opens to show, not 0.
                        const untracked = emailStats.tracking === "none" && s.sent > 0;
                        const fmt = (n: number, rate: number) => (n > 0 ? `${n} (${rate}%)` : "0");
                        return (
                          <>
                            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 14 }}>
                              <div>
                                Sent: <strong style={{ color: "var(--gold, #F9AF36)" }}>{s.sent || 0}</strong>
                                {s.tracked > 0 && s.tracked !== s.sent && <span style={{ fontSize: 12, color: "var(--muted, #A39D98)" }}> ({s.tracked} tracked)</span>}
                              </div>
                              {broken ? (
                                <div data-testid="email-tracking-broken"><strong style={{ color: "var(--red, #e05040)" }}>Tracking not reporting</strong></div>
                              ) : untracked ? (
                                <div data-testid="email-tracking-none" style={{ color: "var(--muted, #A39D98)" }}>Opens/clicks: <strong>not tracked</strong></div>
                              ) : (
                                <>
                                  <div>Opened: <strong style={{ color: "var(--green, #27ae60)" }}>{fmt(s.opened, s.open_rate)}</strong></div>
                                  <div>Clicked: <strong style={{ color: "#5dade2" }}>{fmt(s.clicked, s.click_rate)}</strong></div>
                                </>
                              )}
                            </div>
                            {broken && (
                              <div style={{ fontSize: 12, color: "var(--red, #e05040)", marginTop: 6 }}>
                                Emails sent since Oct 16 have no recorded opens a day or more after sending. The Postmark webhook is probably broken — opens and clicks are not being counted.
                              </div>
                            )}
                            {emailStats.tracking === "incomplete" && (
                              <div data-testid="email-tracking-incomplete" style={{ fontSize: 12, color: "var(--muted, #A39D98)", marginTop: 6 }}>
                                Opens and clicks on emails sent before 6:41 AM CT on Oct 16, 2026 were not recorded, so these numbers undercount.
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {(() => {
                        const parts: string[] = [];
                        const r24 = emailStats.reminders?.reminder_24h;
                        const r6 = emailStats.reminders?.reminder_6h;
                        if (r24 && r24.pending + r24.sent + r24.skipped > 0) parts.push(`24h reminder: ${r24.sent} sent, ${r24.pending} pending, ${r24.skipped} skipped`);
                        if (r6 && r6.pending + r6.sent + r6.skipped > 0) parts.push(`6h reminder: ${r6.sent} sent, ${r6.pending} pending, ${r6.skipped} skipped`);
                        return parts.length ? <div style={{ fontSize: 12, color: "var(--muted, #A39D98)", marginTop: 6 }}>{parts.join(" | ")}</div> : null;
                      })()}
                      {emailStats.send_history?.length > 0 && (() => {
                        const h = emailStats.send_history[0];
                        const d = new Date(h.sent_at);
                        return (
                          <div style={{ fontSize: 12, color: "var(--muted, #A39D98)", marginTop: 4 }}>
                            Last sent: {d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} — {h.email_sent} emails{h.sms_sent ? `, ${h.sms_sent} SMS` : ""}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {editingEvent.id && (
                <div style={styles.attendeesSection}>
                  <div style={styles.attendeesHeader}>
                    <h3 style={styles.sectionTitle}>
                      Registered Attendees ({attendees.filter((a) => a.payment_status === "registered" || a.payment_status === "confirmed" || a.payment_status === "attended").reduce((acc, a) => acc + (a.quantity || 1), 0)})
                    </h3>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={styles.btnSecondary}
                        onClick={() => setShowAddAttendee(!showAddAttendee)}
                      >
                        {showAddAttendee ? "Cancel" : "+ Add Attendee"}
                      </button>
                      <button
                        type="button"
                        style={styles.btnSecondary}
                        disabled={syncingTickets}
                        onClick={() => void handleSyncSquareTickets()}
                        title="Backfill registrations from Square orders"
                      >
                        {syncingTickets ? "Syncing..." : "Sync from Square"}
                      </button>
                    </div>
                  </div>

                  {showAddAttendee && (
                    <div style={styles.addAttendeeForm}>
                      <div style={{ marginBottom: 12 }}>
                        <label style={styles.label}>Search Customer (Name, Email, or Phone)</label>
                        <input
                          type="text"
                          placeholder="Type 2+ characters to search customer database…"
                          value={customerSearchQuery}
                          onChange={(e) => setCustomerSearchQuery(e.target.value)}
                          style={styles.input}
                        />
                        {searchingCustomers && (
                          <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 4 }}>
                            Searching customers…
                          </div>
                        )}
                        {customerSearchResults.length > 0 && (
                          <div
                            style={{
                              background: "var(--surface)",
                              border: "1px solid var(--border)",
                              borderRadius: 8,
                              marginTop: 6,
                              maxHeight: 180,
                              overflowY: "auto",
                            }}
                          >
                            {customerSearchResults.map((c) => (
                              <div
                                key={c.id}
                                style={{
                                  padding: "8px 12px",
                                  borderBottom: "1px solid var(--border)",
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  fontSize: 12,
                                }}
                                onClick={() => handleSelectCustomer(c)}
                              >
                                <div>
                                  <strong>{c.name}</strong>
                                  <div style={{ color: "var(--muted)", fontSize: 11 }}>
                                    {c.email || c.phone || "No contact"}
                                  </div>
                                </div>
                                {c.is_c33 && (
                                  <span
                                    style={{
                                      background: "rgba(249,175,54,0.15)",
                                      color: "var(--gold)",
                                      fontSize: 10,
                                      fontWeight: 800,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    C33
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {selectedCustomerId ? (
                        <div
                          style={{
                            background: "rgba(249,175,54,0.08)",
                            border: "1px solid rgba(249,175,54,0.3)",
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: 12,
                          }}
                        >
                          <div>
                            Selected: <strong>{attendeeName}</strong> ({attendeeEmail || attendeePhone || "No contact"})
                          </div>
                          <button
                            type="button"
                            style={{
                              background: "transparent",
                              border: 0,
                              color: "var(--muted)",
                              cursor: "pointer",
                              fontSize: 11,
                              textDecoration: "underline",
                            }}
                            onClick={() => {
                              setSelectedCustomerId(null);
                              setAttendeeName("");
                              setAttendeeEmail("");
                              setAttendeePhone("");
                            }}
                          >
                            Change
                          </button>
                        </div>
                      ) : null}

                      <div className="station-form-grid" style={{ ...styles.formGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
                        <div style={styles.fieldGroup}>
                          <label style={styles.label}>Ticket Tier</label>
                          <select
                            value={attendeeTier}
                            onChange={(e) => setAttendeeTier(e.target.value)}
                            style={styles.select}
                          >
                            <option value="general">General Public</option>
                            <option value="c33_member">C33 Member</option>
                            <option value="comp">Comp / Guest of Honor</option>
                          </select>
                        </div>
                        <div style={styles.fieldGroup}>
                          <label style={styles.label}>Quantity</label>
                          <select
                            value={attendeeQty}
                            onChange={(e) => setAttendeeQty(parseInt(e.target.value, 10) || 1)}
                            style={styles.select}
                          >
                            {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                              <option key={n} value={n}>
                                {n} ticket{n > 1 ? "s" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                          <label style={styles.label}>Staff Notes (optional)</label>
                          <input
                            type="text"
                            placeholder="VIP, dietary notes, etc."
                            value={attendeeNotes}
                            onChange={(e) => setAttendeeNotes(e.target.value)}
                            style={styles.input}
                          />
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                        <button
                          type="button"
                          style={styles.btnPrimary}
                          disabled={submittingAttendee || !selectedCustomerId}
                          onClick={handleAddAttendee}
                        >
                          {submittingAttendee ? "Registering..." : "Confirm Guest"}
                        </button>
                      </div>
                    </div>
                  )}

                  {loadingAttendees ? (
                    <div style={styles.loaderSmall}>Loading attendee roster…</div>
                  ) : attendees.length === 0 ? (
                    <div style={styles.emptyRoster}>No guests registered yet.</div>
                  ) : (
                    <div style={styles.rosterList}>
                      {attendees.map((a) => {
                        const isSignal = a.payment_status === "intent" || a.ticket_tier === "intent";
                        const isRefunded = a.payment_status === "refunded" || a.payment_status === "cancelled";
                        return (
                          <div
                            key={a.id}
                            style={{
                              ...styles.rosterRow,
                              opacity: isRefunded ? 0.5 : 1,
                            }}
                          >
                            <div>
                              <strong>{a.customer_name}</strong> ({a.quantity} ticket{a.quantity > 1 ? "s" : ""})
                              <div style={styles.rosterSub}>
                                {isSignal ? (
                                  <span style={{ color: "#5dade2", fontWeight: 700 }}>Intent Signal · </span>
                                ) : (
                                  `${a.ticket_tier} · `
                                )}
                                {a.customer_email || a.customer_phone || "No contact"} · Status: {a.payment_status}
                              </div>
                            </div>
                            <span style={styles.rosterDate}>
                              {new Date(a.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {editingEvent.id && (
                <div style={styles.attendeesSection}>
                  <div style={styles.attendeesHeader}>
                    <h3 style={styles.sectionTitle}>Offline Donations &amp; Attendance</h3>
                  </div>
                  {/* minmax(0, 1fr), not 1fr: a bare 1fr track can't shrink below a number input's
                      font-dependent default width, so with wider fonts the third column ran past the
                      modal's scroll panel and "Walk-in / unnamed attendees" was clipped. */}
                  <div className="station-form-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 14 }}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Cash donations ($)</label>
                      <input type="number" step="0.01" min="0" value={offlineCash} onChange={(e) => setOfflineCash(e.target.value)} style={styles.input} aria-label="Offline cash donations" />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Card donations ($)</label>
                      <input type="number" step="0.01" min="0" value={offlineCard} onChange={(e) => setOfflineCard(e.target.value)} style={styles.input} aria-label="Offline card donations" />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Walk-in / unnamed attendees</label>
                      <input type="number" step="1" min="0" value={manualAttendees} onChange={(e) => setManualAttendees(e.target.value)} style={styles.input} aria-label="Manual attendee count" />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
                    <button type="button" style={styles.btnSmall} disabled={offlineSaving} onClick={() => void handleSaveOfflineTotals()}>
                      {offlineSaving ? "Saving..." : "Save Totals"}
                    </button>
                    {offlineStatus && (
                      <span role="status" style={{ fontSize: 13, color: offlineStatus.kind === "ok" ? "var(--green, #27ae60)" : "var(--red, #e05040)" }}>{offlineStatus.text}</span>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.modalActions}>
                {editingEvent.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() => handleCancelEvent(editingEvent.id!, editingEvent.title || "Event")}
                  >
                    Cancel Event
                  </button>
                )}
                {editingEvent.id && (
                  <button type="button" style={styles.btnSecondary} onClick={handleDuplicateEvent}>
                    Duplicate Event
                  </button>
                )}
                <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsFormOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? "Saving..." : editingEvent.id ? "Save Changes" : "Publish Event"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dedicated Guest Roster & Ticket Check-In Modal */}
      {rosterModalEvent && (
        <div style={styles.modalBackdrop} onClick={() => setRosterModalEvent(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={styles.typeBadge}>{rosterModalEvent.event_type}</span>
                  <span
                    style={{
                      ...styles.statusBadge,
                      background:
                        rosterModalEvent.status === "published"
                          ? "rgba(39, 174, 96, 0.15)"
                          : rosterModalEvent.status === "draft"
                          ? "rgba(249, 175, 54, 0.15)"
                          : "rgba(163, 157, 152, 0.15)",
                      color:
                        rosterModalEvent.status === "published"
                          ? "#86efac"
                          : rosterModalEvent.status === "draft"
                          ? "var(--gold)"
                          : "var(--muted)",
                    }}
                  >
                    {rosterModalEvent.status}
                  </span>
                </div>
                <h2 style={{ ...styles.modalTitle, fontSize: 20 }}>
                  {rosterModalEvent.title} — Guest Roster
                </h2>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <span>📅 {rosterModalEvent.start_at ? new Date(rosterModalEvent.start_at).toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Date TBD"}</span>
                  {rosterModalEvent.start_at && <span>🕒 {formatTimeLabel(formatToChicagoDateTime(rosterModalEvent.start_at).timeStr)}</span>}
                  <span>📍 {rosterModalEvent.location || "Sentinel Library"}</span>
                </div>
              </div>
              <button style={styles.modalClose} onClick={() => setRosterModalEvent(null)}>
                &times;
              </button>
            </div>

            {/* Everything between the header and the footer scrolls as one body. The modal is capped
                at 90vh; before, nothing scrolled the overflow, so on a landscape phone the last
                "Check In" and, with "+ Add Guest" open, "Close Roster" were below the screen. */}
            <div style={styles.rosterBody}>
            {/* Metrics Overview Bar */}
            <div style={styles.rosterMetricsBar}>
              <div style={styles.rosterMetricCard}>
                <div style={styles.rosterMetricNum}>
                  {attendees.filter((a) => a.payment_status === "registered" || a.payment_status === "confirmed" || a.payment_status === "attended").reduce((acc, a) => acc + (a.quantity || 1), 0)}
                </div>
                <div style={styles.rosterMetricLabel}>Tickets Sold</div>
              </div>
              <div style={styles.rosterMetricCard}>
                <div style={{ ...styles.rosterMetricNum, color: "#86efac" }}>
                  {attendees.filter((a) => a.payment_status === "attended").reduce((acc, a) => acc + (a.quantity || 1), 0)}
                </div>
                <div style={styles.rosterMetricLabel}>Checked In</div>
              </div>
              <div style={styles.rosterMetricCard}>
                <div style={{ ...styles.rosterMetricNum, color: "var(--gold)" }}>
                  {rosterModalEvent.capacity
                    ? Math.max(
                        0,
                        rosterModalEvent.capacity -
                          attendees.filter((a) => a.payment_status === "registered" || a.payment_status === "confirmed" || a.payment_status === "attended").reduce((acc, a) => acc + (a.quantity || 1), 0)
                      )
                    : "∞"}
                </div>
                <div style={styles.rosterMetricLabel}>Seats Left</div>
              </div>
            </div>

            {/* Quick Actions & Search */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
              <input
                type="text"
                placeholder="🔍 Search guest by name, phone, email, or tier..."
                value={rosterSearch}
                onChange={(e) => setRosterSearch(e.target.value)}
                style={{ ...styles.searchInput, flex: 1 }}
              />
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={() => setShowAddAttendee(!showAddAttendee)}
              >
                {showAddAttendee ? "Hide Form" : "+ Add Guest"}
              </button>
            </div>

            {/* Add Attendee form if open */}
            {showAddAttendee && (
              <div style={styles.addAttendeeForm}>
                <div style={{ marginBottom: 12 }}>
                  <label style={styles.label}>Search Customer (Name, Email, or Phone)</label>
                  <input
                    type="text"
                    placeholder="Type 2+ characters to search customer database…"
                    value={customerSearchQuery}
                    onChange={(e) => setCustomerSearchQuery(e.target.value)}
                    style={styles.input}
                  />
                  {searchingCustomers && (
                    <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 4 }}>
                      Searching customers…
                    </div>
                  )}
                  {customerSearchResults.length > 0 && (
                    <div
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        marginTop: 6,
                        maxHeight: 180,
                        overflowY: "auto",
                      }}
                    >
                      {customerSearchResults.map((c) => (
                        <div
                          key={c.id}
                          style={{
                            padding: "8px 12px",
                            borderBottom: "1px solid var(--border)",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: 12,
                          }}
                          onClick={() => handleSelectCustomer(c)}
                        >
                          <div>
                            <strong>{c.name}</strong>
                            <div style={{ color: "var(--muted)", fontSize: 11 }}>
                              {c.email || c.phone || "No contact"}
                            </div>
                          </div>
                          {c.is_c33 && (
                            <span
                              style={{
                                background: "rgba(249,175,54,0.15)",
                                color: "var(--gold)",
                                fontSize: 10,
                                fontWeight: 800,
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}
                            >
                              C33
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedCustomerId && (
                  <div
                    style={{
                      background: "rgba(249,175,54,0.08)",
                      border: "1px solid rgba(249,175,54,0.3)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      marginBottom: 12,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 12,
                    }}
                  >
                    <div>
                      Selected: <strong>{attendeeName}</strong> ({attendeeEmail || attendeePhone || "No contact"})
                    </div>
                    <button
                      type="button"
                      style={{
                        background: "transparent",
                        border: 0,
                        color: "var(--muted)",
                        cursor: "pointer",
                        fontSize: 11,
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        setSelectedCustomerId(null);
                        setAttendeeName("");
                        setAttendeeEmail("");
                        setAttendeePhone("");
                      }}
                    >
                      Change
                    </button>
                  </div>
                )}

                <div className="station-form-grid" style={{ ...styles.formGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Ticket Tier</label>
                    <select
                      value={attendeeTier}
                      onChange={(e) => setAttendeeTier(e.target.value)}
                      style={styles.select}
                    >
                      <option value="general">General Public</option>
                      <option value="c33_member">C33 Member</option>
                      <option value="comp">Comp / Guest of Honor</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Quantity</label>
                    <select
                      value={attendeeQty}
                      onChange={(e) => setAttendeeQty(parseInt(e.target.value, 10) || 1)}
                      style={styles.select}
                    >
                      {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                        <option key={n} value={n}>
                          {n} ticket{n > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                    <label style={styles.label}>Staff Notes (optional)</label>
                    <input
                      type="text"
                      placeholder="VIP, seating preference, dietary notes..."
                      value={attendeeNotes}
                      onChange={(e) => setAttendeeNotes(e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <button
                    type="button"
                    style={styles.btnPrimary}
                    disabled={submittingAttendee || !selectedCustomerId}
                    onClick={handleAddAttendee}
                  >
                    {submittingAttendee ? "Registering..." : "Confirm Guest"}
                  </button>
                </div>
              </div>
            )}

            {/* Attendee Roster List. Its 220px minimum is capped at 40vh so that on a short
                screen it fits inside the body above (220px on any screen 550px or taller). */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: "min(220px, 40vh)", paddingRight: 4 }}>
              {loadingAttendees ? (
                <div style={styles.loaderSmall}>Loading guest tickets & attendees…</div>
              ) : filteredRosterAttendees.length === 0 ? (
                <div style={styles.emptyRoster}>
                  {rosterSearch ? "No guests match your search." : "No ticket purchasers or guests registered yet."}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {filteredRosterAttendees.map((a) => {
                    const isAttended = a.payment_status === "attended";
                    const isRefunded = a.payment_status === "refunded" || a.payment_status === "cancelled";

                    return (
                      <div
                        key={a.id}
                        style={{
                          background: isAttended ? "rgba(39, 174, 96, 0.08)" : "var(--bg, #0C0907)",
                          border: `1px solid ${isAttended ? "rgba(39, 174, 96, 0.3)" : "var(--border, #231D19)"}`,
                          borderRadius: 8,
                          padding: "12px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          opacity: isRefunded ? 0.45 : 1,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <strong style={{ fontSize: 15, color: "var(--text, #F7F5F0)" }}>
                              {a.customer_name}
                            </strong>
                            <span
                              style={{
                                background: "rgba(249, 175, 54, 0.15)",
                                color: "var(--gold, #F9AF36)",
                                padding: "2px 8px",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              🎟️ {a.quantity} {a.quantity > 1 ? "Tickets" : "Ticket"}
                            </span>
                            <span
                              style={{
                                background: a.ticket_tier === "c33_member" ? "rgba(249,175,54,0.15)" : a.ticket_tier === "comp" ? "rgba(163, 157, 152, 0.2)" : "rgba(74, 143, 217, 0.15)",
                                color: a.ticket_tier === "c33_member" ? "var(--gold)" : a.ticket_tier === "comp" ? "var(--muted)" : "#5dade2",
                                padding: "2px 8px",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "capitalize",
                              }}
                            >
                              {a.ticket_tier.replace("_", " ")}
                            </span>
                          </div>

                          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {a.customer_email && <span>📧 {a.customer_email}</span>}
                            {a.customer_phone && <span>📞 {a.customer_phone}</span>}
                            <span>Registered: {new Date(a.created_at).toLocaleDateString()}</span>
                            <span>Status: <strong>{a.payment_status}</strong></span>
                          </div>

                          {a.notes && (
                            <div style={{ fontSize: 12, color: "var(--gold)", marginTop: 4, fontStyle: "italic" }}>
                              📝 Note: {a.notes}
                            </div>
                          )}
                        </div>

                        {/* Check-In Button */}
                        <div>
                          <button
                            type="button"
                            style={{
                              background: isAttended ? "#27ae60" : "transparent",
                              border: `1px solid ${isAttended ? "#27ae60" : "var(--gold)"}`,
                              color: isAttended ? "#fff" : "var(--gold)",
                              padding: "6px 14px",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              whiteSpace: "nowrap",
                            }}
                            onClick={() => handleToggleCheckIn(a.id)}
                          >
                            {isAttended ? "✓ Checked In" : "Check In"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </div>

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={() => setRosterModalEvent(null)}
              >
                Close Roster
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 20px 60px",
    maxWidth: 1150,
    margin: "0 auto",
    color: "var(--text, #F7F5F0)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    gap: 16,
    flexWrap: "wrap",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  toast: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid",
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 600,
  },
  toolbar: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 20,
  },
  filterPills: {
    display: "flex",
    gap: 6,
    overflowX: "auto",
    paddingBottom: 4,
  },
  pillBtn: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  pillBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    borderColor: "var(--gold, #F9AF36)",
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    color: "var(--text, #F7F5F0)",
    fontSize: 14,
    outline: "none",
  },
  eventsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 16,
  },
  eventCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 18,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    transition: "border-color 0.15s ease",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  typeBadge: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--gold, #F9AF36)",
  },
  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
    textTransform: "uppercase",
  },
  cardTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 17,
    margin: 0,
    color: "var(--text, #F7F5F0)",
    lineHeight: 1.3,
  },
  dateTimeRow: {
    display: "flex",
    gap: 12,
    fontSize: 13,
    color: "var(--text, #F7F5F0)",
    fontWeight: 600,
  },
  locationText: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "auto",
    paddingTop: 10,
    borderTop: "1px solid var(--border, #231D19)",
  },
  cardPrice: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
  },
  c33Tag: {
    fontSize: 11,
    color: "var(--gold, #F9AF36)",
    marginLeft: 6,
    fontWeight: 600,
  },
  capacityTag: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  loader: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
  },
  emptyState: {
    gridColumn: "1/-1",
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    // Must sit above AdminBottomNav (zIndex 990, fixed, 56px + safe-area). At 100 the phone
    // nav drew over the bottom of the form and swallowed taps on fields scrolled under it.
    // Stays below the publish menu (9999), alert banner (9999) and update prompt (10050).
    zIndex: 1000,
  },
  modalContent: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 14,
    width: "100%",
    maxWidth: 760,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    padding: 24,
    boxShadow: "0 20px 50px rgba(0,0,0,0.8)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "1px solid var(--border, #231D19)",
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    margin: 0,
    color: "var(--text, #F7F5F0)",
  },
  modalClose: {
    background: "transparent",
    border: 0,
    color: "var(--muted, #A39D98)",
    fontSize: 24,
    cursor: "pointer",
    // Normal line height, not 1: at 1 the "×" glyph's ink ran 3px past the button's own box
    // at 768/1024 (mobile-states edit-event).
    lineHeight: "normal",
  },
  formScroll: {
    flex: 1,
    overflowY: "auto",
    paddingRight: 4,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--muted, #A39D98)",
  },
  input: {
    padding: "9px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
  },
  select: {
    padding: "9px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
  },
  textarea: {
    padding: "9px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
  },
  mdBtn: {
    padding: "3px 8px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 4,
    color: "var(--text, #F7F5F0)",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    minWidth: 28,
  },
  hint: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    lineHeight: 1.4,
  },
  previewBox: {
    padding: 12,
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    fontSize: 13,
    lineHeight: 1.5,
    minHeight: 100,
    whiteSpace: "pre-wrap",
  },
  attendeesSection: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid var(--border, #231D19)",
  },
  attendeesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 15,
    margin: 0,
    color: "var(--gold, #F9AF36)",
  },
  addAttendeeForm: {
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: 14,
    marginBottom: 14,
  },
  loaderSmall: {
    padding: 16,
    textAlign: "center",
    color: "var(--muted, #A39D98)",
    fontSize: 12,
  },
  emptyRoster: {
    padding: 16,
    textAlign: "center",
    color: "var(--muted, #A39D98)",
    fontSize: 12,
    background: "var(--bg, #0C0907)",
    borderRadius: 6,
  },
  rosterBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  rosterList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 200,
    overflowY: "auto",
  },
  rosterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "var(--bg, #0C0907)",
    borderRadius: 6,
    fontSize: 13,
  },
  rosterSub: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    marginTop: 2,
  },
  rosterDate: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
  },
  // Wraps instead of squeezing: on a phone, editing an event puts four buttons in this row and
  // "Cancel Event" / "Duplicate Event" shrank under their own labels. rowGap only spaces wrapped
  // lines, so a row that fits (768+) is unchanged.
  modalActions: {
    display: "flex",
    flexWrap: "wrap",
    rowGap: 10,
    alignItems: "center",
    marginTop: 20,
    paddingTop: 14,
    borderTop: "1px solid var(--border, #231D19)",
  },
  btnPrimary: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    padding: "8px 18px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnSecondary: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSmall: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    cursor: "pointer",
  },
  btnDanger: {
    background: "transparent",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "var(--red, #FF8A7A)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  cardActionsRow: {
    display: "flex",
    gap: 8,
    marginTop: 6,
    alignItems: "center",
  },
  cardRosterBtn: {
    flex: 1,
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    color: "var(--gold, #F9AF36)",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.15s ease",
  },
  cardEditBtn: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  rosterMetricsBar: {
    display: "grid",
    // Three across when they fit (tablet), wrapping to 2+1 or a single column on phones.
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  rosterMetricCard: {
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: "12px 14px",
    textAlign: "center",
  },
  rosterMetricNum: {
    fontSize: 22,
    fontWeight: 800,
    color: "var(--text, #F7F5F0)",
    fontFamily: "Georgia, serif",
    lineHeight: 1.1,
  },
  rosterMetricLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    color: "var(--muted, #A39D98)",
    fontWeight: 700,
    marginTop: 4,
  },
};








